'use client'

import { Rule } from 'antd/es/form'
import dayjs from 'dayjs'
import { TFunction } from 'i18next'
import { AttachmentType } from './attachment/AttachmentFile'

export const integerRule = (t: TFunction<string, undefined>): Rule => ({
  type: 'integer',
  message: t('errors.integer'),
})

export const minLengthRule = (
  length: number,
  t: TFunction<string, undefined>
): Rule => ({
  pattern: new RegExp(`.{${length},}`, 'g'),
  message: t('errors.minLength', { length }),
})

export const capsRule = (t: TFunction<string, undefined>): Rule => ({
  pattern: /[A-Z]+/g,
  message: t('errors.caps'),
})

export const lowercaseRule = (t: TFunction<string, undefined>): Rule => ({
  pattern: /[a-z]+/g,
  message: t('errors.lowercase'),
})

export const containsNumberRule = (t: TFunction<string, undefined>): Rule => ({
  pattern: /\d+/g,
  message: t('errors.containsNumber'),
})

export const containsSymbolRule = (t: TFunction<string, undefined>): Rule => ({
  pattern: /\W+/g,
  message: t('errors.containsSymbol'),
})

export const numberRule = (t: TFunction<string, undefined>): Rule => ({
  type: 'number',
  message: t('errors.number'),
})

export const stringRule = (t: TFunction<string, undefined>): Rule => ({
  type: 'string',
  message: t('errors.string'),
})

export const versionRule = (t: TFunction<string, undefined>): Rule => ({
  pattern: /^\d+\.\d+\.\d+$/,
  message: t('errors.version'),
})

export const urlRule = (t: TFunction<string, undefined>): Rule => ({
  type: 'url',
  message: t('errors.url'),
})

export const emailRule = (t: TFunction<string, undefined>): Rule => ({
  type: 'email',
  message: t('errors.email'),
})

export const requiredRule = (t: TFunction<string, undefined>): Rule => ({
  required: true,
  message: t('errors.required'),
})

export const dateRule = (t: TFunction<string, undefined>): Rule => ({
  type: 'object',
  required: true,
  message: t('errors.date'),
})

export const dateRangeRule = (
  minDate: dayjs.Dayjs,
  maxDate: dayjs.Dayjs,
  t: TFunction<string, undefined>
): Rule => ({
  validator: (_, value) => {
    if (!value) return Promise.reject(new Error(t('errors.dateRange')))
    if (value.isBefore(minDate, 'day') || value.isAfter(maxDate, 'day')) {
      return Promise.reject(
        new Error(
          t('errors.dateRangeBetween', {
            minDate: minDate.format('YYYY-MM-DD'),
            maxDate: maxDate.format('YYYY-MM-DD'),
          })
        )
      )
    }
    return Promise.resolve()
  },
})

export const acceptedRule = (t: TFunction<string, undefined>): Rule => ({
  validator: (_, value) =>
    value ? Promise.resolve() : Promise.reject(new Error(t('errors.accepted'))),
})

export const nestedCascaderExceptAddressRule = (
  minDepth = 2,
  t: TFunction<string, undefined>
): Rule => ({
  validator: (_, value) =>
    value && value.length === 1 && value[0] === 'customaddress'
      ? Promise.resolve()
      : value.length >= minDepth
        ? Promise.resolve()
        : Promise.reject(new Error(t('errors.nestedCascaderExceptAddress'))),
})

export const matchValueRule = (
  expectedValue: string,
  t: TFunction<string, undefined>
): Rule => ({
  validator: (_, value) =>
    value === expectedValue
      ? Promise.resolve()
      : Promise.reject(new Error(t('errors.matchValue'))),
})

export const notMatchValuesRule = (
  reserved: string[],
  t: TFunction<string, undefined>
): Rule => ({
  validator: (_, value) =>
    reserved.includes(value)
      ? Promise.reject(new Error(t('errors.notMatchValues')))
      : Promise.resolve(),
})

export const lessOneIsChecked = (
  array: any[],
  t: TFunction<string, undefined>
): Rule => ({
  validator: () =>
    array.length > 0
      ? Promise.resolve()
      : Promise.reject(new Error(t('errors.lessOneIsChecked'))),
})

export const doesntExceedSize = (
  attachmentTypes: AttachmentType[],
  t: TFunction<string, undefined>
): Rule => ({
  validator: (_, value) => {
    if (!value) return Promise.reject(new Error(t('errors.required')))
    const file = value[value.length - 1]
    if (!file) return Promise.resolve()
    const attachmentType = attachmentTypes.find((at) =>
      at.accept.includes(file.type)
    )
    return attachmentType && file.size > attachmentType.maxSize
      ? Promise.reject(new Error(t('errors.doesntExceedSize')))
      : Promise.resolve()
  },
})
